import { useViewportWidth } from '@xstyled/styled-components';
import React from 'react';
import { th, useTheme } from './theme';
/** Returns CSS media string with appropriate min-width and max-width values. */
export const mediaBetween = (minWidth, maxWidth) => (props) => `@media (min-width: ${th.screen(minWidth)(props)}) and (max-width: ${th.screen(maxWidth)(props)})`;
/** Returns CSS media string with appropriate max-width value */
export const mediaDown = (width) => (props) => {
    return `@media (max-width: ${th.screen(width)(props)})`;
};
/** Returns CSS media string with appropriate min-width value. */
export const mediaUp = (width) => (props) => `@media (min-width: ${th.screen(width)(props)})`;
/**
 * Renders its content when viewport width is at or above a provided breakpoint.
 * Optionally, direction can be set to down, or below, the breakpoint.
 */
export const RenderOnScreen = ({ children, maxWidth, minWidth }) => {
    if (minWidth && maxWidth) {
        return (React.createElement(RenderBetween, { maxWidth: maxWidth, minWidth: minWidth }, children));
    }
    if (minWidth) {
        return React.createElement(RenderOver, { width: minWidth }, children);
    }
    if (maxWidth) {
        return React.createElement(RenderUnder, { width: maxWidth }, children);
    }
    return React.createElement(React.Fragment, null, children);
};
const RenderBetween = ({ children, maxWidth, minWidth }) => {
    const isBetween = useBetween(minWidth, maxWidth);
    return isBetween ? React.createElement(React.Fragment, null, children) : null;
};
const RenderOver = ({ children, width }) => {
    const isOver = useUp(width);
    return isOver ? React.createElement(React.Fragment, null, children) : null;
};
const RenderUnder = ({ children, width }) => {
    const isUnder = useDown(width);
    return isUnder ? React.createElement(React.Fragment, null, children) : null;
};
/** /!\ No need to export it, keep it internal */
/** Returns viewport width and provided screen width as a number. */
function useMedia(width) {
    const theme = useTheme();
    const viewportWidth = useViewportWidth();
    const valueString = th.screen(width)({ theme });
    const value = Number(valueString.replace('px', ''));
    return { value, viewportWidth };
}
/** Returns true if current viewport width is smaller than provided width. */
export function useDown(width) {
    const { value, viewportWidth } = useMedia(width);
    return viewportWidth !== null && value !== null && viewportWidth < value;
}
/** Returns true if current viewport width is larger than or equal to provided width. */
export function useUp(width) {
    const { value, viewportWidth } = useMedia(width);
    return viewportWidth !== null && value !== null && viewportWidth >= value;
}
/** Returns true if current viewport width is between provided min and max widths. */
export function useBetween(minWidth, maxWidth) {
    const isDown = useDown(maxWidth);
    const isUp = useUp(minWidth);
    return isDown && isUp;
}
/** Return the width of the viewport. */
export { useViewportWidth };
